import { useCallback, useEffect, useState } from 'react'
import { FileSpreadsheet, RefreshCw } from 'lucide-react'
// @ts-ignore
import * as XLSX from 'xlsx-js-style'
import { mensajeError, obtener } from '../api/client'
import { alerta } from '../utils/alertas'
import type { ReporteResumen } from '../types'

export default function Reportes() {
  const [resumenes, setResumenes] = useState<ReporteResumen[]>([])
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      setResumenes(await obtener<ReporteResumen[]>('reportes'))
    } catch (error) {
      await alerta.error('No se pudo cargar los reportes', mensajeError(error))
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const exportarExcel = () => {
    if (resumenes.length === 0) {
      void alerta.toast('No hay datos para exportar')
      return
    }

    const aoa: (string | number)[][] = [
      ['Titulo', 'Total'],
      ...resumenes.map((r) => [r.titulo, r.total])
    ]

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = [{ wch: 40 }, { wch: 15 }]

    const r = XLSX.utils.decode_range(ws['!ref']!)
    for (let row = r.s.r; row <= r.e.r; row++) {
      for (let col = r.s.c; col <= r.e.c; col++) {
        const ref = XLSX.utils.encode_cell({ r: row, c: col })
        const cell = ws[ref]
        if (!cell) continue

        if (row === 0) {
          cell.s = {
            font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12, name: 'Arial' },
            fill: { fgColor: { rgb: '0F766E' }, patternType: 'solid' },
            alignment: { horizontal: 'center', vertical: 'center' },
          }
        } else {
          cell.s = {
            fill: { fgColor: { rgb: row % 2 === 0 ? 'F0FDF4' : 'FFFFFF' }, patternType: 'solid' },
            alignment: { vertical: 'center', horizontal: col === 1 ? 'right' : 'left' },
          }
        }
      }
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Resumenes')

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([new Uint8Array(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reportes-resumen-${new Date().toISOString().slice(0, 10)}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="anima-entrada space-y-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="m-0 text-2xl font-bold text-texto">Reportes</h2>
          <p className="text-sm text-texto-suave">Resumenes operativos del sistema.</p>
        </div>
        <div className="flex items-center gap-2 mr-13">
          <button
            onClick={() => void cargar()}
            className="btn btn-plano btn-icono"
            title="Recargar"
          > Recargar
            <RefreshCw size={17} className={cargando ? 'anima-pulso' : ''} />
          </button>
          <button
            onClick={() => void exportarExcel()}
            className="btn inline-flex items-center gap-2"
          >
            <FileSpreadsheet size={17} /> Exportar Excel
          </button>
        </div>
      </div>

      {cargando ? (
        <div className="p-12 text-center text-texto-suave">Cargando...</div>
      ) : resumenes.length === 0 ? (
        <div className="p-12 text-center text-texto-suave">No hay reportes disponibles.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {resumenes.map((r, i) => (
            <div key={i} className="rounded-xl border border-borde bg-superficie-soft p-4 shadow-sm">
              <h3 className="text-lg font-bold text-texto">{r.titulo}</h3>
              <p className="mt-1 text-3xl font-bold text-accent">{r.total.toLocaleString('es-MX')}</p>
              {r.agrupado && (
                <div className="mt-3 space-y-1">
                  {Object.entries(r.agrupado).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm text-texto-suave">
                      <span>{k}</span>
                      <span className="font-medium text-texto">{v.toLocaleString('es-MX')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
